// Iter54 — Trial / freemium banner shown at the top of the main content area.
//
// Polls GET /api/auth/me/tier on mount + every 5min. Shows:
//   * Trial countdown banner ("Full access trial — N days remaining") when
//     `tier === "trial"` and trial is active.
//   * Freemium usage warning when ANY quota is ≥80% utilised.
//   * Hidden when the tenant is on the `full` tier and no warnings.
//
// Backend source of truth: `GET /api/auth/me/tier`.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { X, Sparkles, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mono = (size, color = "var(--text, #f0f2f8)", weight = 400, ls) => ({
  fontFamily: "DM Mono, monospace", fontSize: size, color, fontWeight: weight, letterSpacing: ls,
});

const DISMISS_KEY = "sitroom_tier_banner_dismissed_v1";

export const TierBanner = () => {
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me/tier`);
      setInfo(data);
    } catch (_) { /* swallow */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5 * 60_000);
    return () => clearInterval(t);
  }, []);

  if (!info || dismissed) return null;

  const { tier, days_remaining: days, trial_active, usage = {} } = info;
  const highUsage = Object.entries(usage).filter(([, u]) => u.limit && u.used / u.limit >= 0.8);

  if (tier === "trial" && trial_active) {
    return (
      <Banner
        color="#9ec3ec"
        bg="rgba(24,95,165,0.12)"
        border="rgba(24,95,165,0.45)"
        icon={<Sparkles size={14} />}
        message={
          <>
            <strong>Full access trial</strong> — {days} {days === 1 ? "day" : "days"} remaining.
            {" "}Upgrade to keep all features after expiry.
          </>
        }
        testId="tier-banner-trial"
      />
    );
  }

  if (tier === "freemium" && highUsage.length) {
    const [, u] = highUsage[0];
    const pct = Math.round((u.used / u.limit) * 100);
    return (
      <Banner
        color="#f3b14a"
        bg="rgba(243,177,74,0.10)"
        border="rgba(243,177,74,0.40)"
        icon={<AlertTriangle size={14} />}
        message={
          <>
            Freemium quota <strong>{pct}% used</strong> ({u.used}/{u.limit}).
            {" "}Upgrade to full SITROOM for unlimited access.
          </>
        }
        testId="tier-banner-freemium-warn"
        onDismiss={() => {
          try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
          setDismissed(true);
        }}
      />
    );
  }

  if (tier === "freemium") {
    return (
      <Banner
        color="#c89adb"
        bg="rgba(155,89,182,0.08)"
        border="rgba(155,89,182,0.30)"
        icon={<Sparkles size={14} />}
        message={
          <>
            You're on the <strong>freemium</strong> tier. Upgrade to unlock
            unlimited FMEAs · 8D · incidents + SITREP · AI scorer · webhooks.
          </>
        }
        testId="tier-banner-freemium"
        onDismiss={() => {
          try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
          setDismissed(true);
        }}
      />
    );
  }

  return null;
};

const Banner = ({ color, bg, border, icon, message, testId, onDismiss }) => (
  <div
    className="rounded-md px-3 py-2 mb-4 flex items-center gap-2"
    style={{ background: bg, border: `1px solid ${border}`, ...mono(11, color, 600, "0.3px") }}
    data-testid={testId}
  >
    <span style={{ color }}>{icon}</span>
    <span style={{ flex: 1, color: "var(--text, #f0f2f8)" }}>{message}</span>
    <Link to="/settings/tier" style={mono(10, color, 700, "0.4px")} data-testid={`${testId}-cta`}>
      Manage →
    </Link>
    {onDismiss && (
      <button
        type="button" onClick={onDismiss}
        style={{ background: "transparent", border: "none", color, cursor: "pointer", padding: 2 }}
        data-testid={`${testId}-dismiss`} aria-label="Dismiss"
      ><X size={12} /></button>
    )}
  </div>
);
